import { motion } from 'framer-motion';
import { QuizQuestion } from '@shared/types';
import { Check } from 'lucide-react';

interface MultipleChoiceProps {
  question: QuizQuestion;
  value: string[];
  onChange: (value: string[]) => void;
  onFinished?: (value: string[]) => void;
}

export default function MultipleChoice({ question, value = [], onChange, onFinished }: MultipleChoiceProps) {
  const maxSelections = question.maxSelections || Infinity;
  
  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else  {
      const newValue = [optionValue, ...value].slice(0, maxSelections);
      onChange(newValue);
      
      // Finished when max selections reached
      if (newValue.length === maxSelections && onFinished) {
        onFinished(newValue);
      }
    }
  };

  return (
    <div className="space-y-4">
      {question.maxSelections && (
        <p className="text-sm text-dark-muted">
          Selected: {value.length} / {maxSelections}
        </p>
      )}
      
      <div className="space-y-2">
        {question.options?.map((option) => {
          const isSelected = value.includes(option.value);
          const canSelect = true;
          
          return (
            <button
              key={option.value}
              onClick={() => handleToggle(option.value)}
              disabled={!canSelect}
              className={`
                w-full p-4 rounded-xl text-left transition-all
                ${isSelected
                  ? 'select-option-selected'
                  : 'select-option'
                }
                ${!canSelect ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {option.emoji && (
                    <span className="text-2xl">{option.emoji}</span>
                  )}
                  <span className="font-medium">{option.label}</span>
                </div>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center"
                  >
                    <Check className="w-4 h-4 text-white" />
                  </motion.div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
